/*
 * The MIT License
 *
 * Copyright (c) 2019- Nordic Institute for Interoperability Solutions (NIIS)
 * Copyright (c) 2018 Estonian Information System Authority (RIA),
 * Nordic Institute for Interoperability Solutions (NIIS), Population Register Centre (VRK)
 * Copyright (c) 2015-2017 Estonian Information System Authority (RIA), Population Register Centre (VRK)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import { defineStore } from 'pinia';
import { deepClone } from '@/util/helpers';
import axios from 'axios';
import { Token, TokenPassword } from '@/openapi-types';

export const useToken = defineStore('token', {
  state: () => {
    return {
      expandedTokens: [] as string[],
      tokens: [] as Token[],
      selectedToken: undefined as Token | undefined,
    };
  },
  getters: {
    tokenExpanded: (state) => (id: string) => {
      return state.expandedTokens.includes(id);
    },

    getSortedTokens(state) {
      if (!state.tokens || state.tokens.length === 0) {
        return [];
      }

      // Sort array by id:s so it doesn't jump around. Order of items in the backend reply changes between requests.
      const arr = deepClone(state.tokens).sort((a, b) => {
        if (a.id < b.id) {
          return -1;
        }
        if (a.id > b.id) {
          return 1;
        }

        // equal id:s. (should not happen)
        return 0;
      });

      return arr;
    },
  },

  actions: {
    setTokenHidden(id: string) {
      const index = this.expandedTokens.findIndex((element) => {
        return element === id;
      });

      if (index >= 0) {
        this.expandedTokens.splice(index, 1);
      }
    },

    setTokenExpanded(id: string) {
      const index = this.expandedTokens.findIndex((element) => {
        return element === id;
      });

      if (index === -1) {
        this.expandedTokens.push(id);
      }
    },

    setSelectedToken(token: Token) {
      this.selectedToken = token;
    },

    fetchTokens() {
      return axios
        .get<Token[]>(`/tokens`)
        .then((resp) => {
          this.tokens = resp.data;
        })
        .catch((error) => {
          throw error;
        });
    },
    loginToken(id: string, tokenPassword: TokenPassword) {
      return axios
        .put<Token>(`/tokens/${id}/login`, tokenPassword)
        .catch((error) => {
          throw error;
        });
    },
    logoutToken(id: string) {
      return axios.put<Token>(`/tokens/${id}/logout`).catch((error) => {
        throw error;
      });
    },
  },
});
